import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useEffect, useState } from "react";
import { IMembershipDetailsTabProps } from "../../interface";
import { useMembershipUsageDetailsAPI } from "@/app/hooks/api_hooks/Membership/useMembershipUsageDetailsAPI";
import { useMembershipVisitDetailsAPI } from "@/app/hooks/api_hooks/Membership/useMembershipVisitDetailsAPI";
import { IMembershipState } from "@/app_redux/reducers/slices/auth/membership.slice";
import { IoIosArrowForward } from "react-icons/io";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";

const MembershipDetailsTab: React.FC<IMembershipDetailsTabProps> = (_props) => {
  const { NO_APPOINTMENTS } = useImageConstants();
  const { getRouteKey } = useRouter();
  const { routeState } = useRouteInfo(getRouteKey("CUSTOMER_PROFILE", "id"));
  const routeId = getRouteKey("MEMBERSHIP_SETUP", "id");
  const { routeState: membership } = useRouteInfo(routeId);
  const { membershipUsageDetails, membershipVisitDetails } = membership.state as IMembershipState;
  const { customerId, petId } = routeState.state as ICustomerProfileState;
  const { execute: fetchMembershipUsageData } = useMembershipUsageDetailsAPI();
  const { execute: fetchMembershipVisitData } = useMembershipVisitDetailsAPI();
  const { execute: clickLogAPI } = useClickLogAPI();
  const userLogInfo = useAppSelector((state) => state.auth?.user?.email);
  const mappedClinicId = useAppSelector((state) => state.root.UIStates.mappedClinicId);
  const [expandedMembershipId, setExpandedMembershipId] = useState<string | null>(null);

  useEffect(() => {
    if (customerId) {
      fetchMembershipUsageData(customerId);
    }
  }, [customerId]);

  const handleToggleDetails = async (item) => {
    const id = item?.user_membership_id;
    if (!id) return;
    const isClosing = expandedMembershipId === id;

    const constructedBody: IClickLogBody = {
      event_type: isClosing ? "MEMBERSHIP_DETAILS_CLOSED" : "MEMBERSHIP_DETAILS_OPENED",
      vms_user_id: userLogInfo,
      scr_id: "membership_details_tab",
      clinic_id: mappedClinicId,
      client_id: customerId,
      membership_id: id,
      membership_details: item,
      timestamp: Date.now(),
    };
    clickLogAPI(constructedBody);

    if (isClosing) {
      setExpandedMembershipId(null);
      return;
    }
    setExpandedMembershipId(id);
    await fetchMembershipVisitData(id);
  };

  const handleBookFunction = (item) => {
    const constructedBody: IClickLogBody = {
      event_type: "MEMBERSHIP_BOOK_SLOT_CLICKED",
      vms_user_id: userLogInfo,
      scr_id: "membership_details_tab",
      clinic_id: mappedClinicId,
      client_id: customerId,
      membership_id: item?.user_membership_id,
      membership_details: item,
      timestamp: Date.now(),
    };
    clickLogAPI(constructedBody);

    if (petId) {
      window.open(`/app/slots/available-slots/${customerId}/${petId}`, "_blank");
    }
  };

  const getMembershipStatusStyle = (status: string) => {
    switch (status) {
      case "Active":
        return { backgroundColor: "#DDFFEC", color: "#02632B" };
      case "Expired":
        return { backgroundColor: "#efefef", color: "#40444c" };
      case "Exhausted":
        return { backgroundColor: "#ffe4e4", color: "#d50d25" };
      default:
        return { backgroundColor: "#DDFFEC", color: "#02632B" };
    }
  };

  const handleViewInvoice = (record, item) => {
    const constructedBody: IClickLogBody = {
      event_type: "MEMBERSHIP_VIEW_INVOICE_CLICKED",
      vms_user_id: userLogInfo,
      scr_id: "membership_details_tab",
      clinic_id: mappedClinicId,
      client_id: customerId,
      membership_id: item?.user_membership_id,
      membership_details: item,
      timestamp: Date.now(),
    };
    clickLogAPI(constructedBody);

    window.open(record?.invoice_pdf_url, "_blank");
  };

  return (
    <div className="px-3 sm:px-2 lg:px-2 bg-[#FBFCFD]">
      <div className="sm:flex sm:items-center">
        <div className="flex flex-row justify-between items-start w-full gap-3">
          <div className="w-1/2 sm:flex-auto sm:w-auto">
            <h1 className="text-sm font-semibold leading-6 text-gray-900 pt-0 sm:pt-2">Membership</h1>
            <p className="text-[#727272] font-normal">Click a card to expand and review the full utilisation history</p>
          </div>
          {!!membershipUsageDetails?.memberships?.length && (
            <div className="w-1/2 sm:w-auto pt-0 sm:pt-0 text-right sm:text-left">
              <p className="text-[#727272] font-normal">Total Remaining</p>
              <p className="font-semibold text-sm">
                {membershipUsageDetails?.total_remaining}/
                <span className="text-[#727272] text-xs font-medium">{membershipUsageDetails?.total_limit}</span>
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="border border-[#EFEFEF] rounded-xl overflow-hidden mt-3 bg-white">
        {!membershipUsageDetails?.memberships?.length ? (
          <NoDataFoundComponent
            imageURL={NO_APPOINTMENTS}
            message="No Membership Found."
            description="No membership details are available for the selected customer."
          />
        ) : (
          membershipUsageDetails?.memberships?.map((data, id) => {
            const isExpanded = expandedMembershipId === data?.user_membership_id;
            const isLast = id === (membershipUsageDetails?.memberships?.length ?? 0) - 1;
            return (
              <div key={id}>
                <div
                  className={`px-5 pt-8 flex flex-col sm:flex-row gap-5 ${isExpanded ? "pb-8" : "pb-8"} ${!isExpanded && !isLast ? "border-b border-[#EFEFEF]" : ""}`}
                >
                  <div className="flex justify-between items-start w-full sm:w-[350px] sm:shrink-0">
                    <div>
                      <h1 className="text-[14px] font-semibold">{data?.membership_name}</h1>
                      <p className="flex text-[#494949] mt-1 text-[12px]">
                        {data?.expiry_string}
                        <ul className="list-disc pl-4">
                          <li>{data?.days_left}</li>
                        </ul>
                      </p>
                      <button
                        onClick={() => handleToggleDetails(data)}
                        className="text-[#1B68D3] text-xs font-semibold py-2 px-8 border border-[#1B68D3] text-center rounded-lg mt-3"
                      >
                        {isExpanded ? "View Less" : "View Details"}
                      </button>
                    </div>
                    <div>
                      <p
                        className="text-xs px-2 py-1 font-medium rounded-lg"
                        style={getMembershipStatusStyle(data?.status)}
                      >
                        {data?.status}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-5 flex-1 w-full">
                    {data?.utilization_list?.map((util, idx) => (
                      <div
                        className="border border-[#EFEFEF] flex justify-between items-center w-full sm:w-[250px] min-w-[140px] px-3 py-2 rounded-xl"
                        key={idx}
                      >
                        <div>
                          <p className="text-[#32363C] font-medium mt-2">{util?.voucher_type_text}</p>
                          <p className="text-[#32363C] font-semibold text-sm mt-3">{util?.usage_text}</p>
                        </div>
                        <div>
                          <button
                            onClick={() => handleBookFunction(data)}
                            className="bg-[#FFEDED] text-[#F05B72] px-2 py-1 rounded-lg font-medium text-sm mt-2 flex items-center gap-1"
                          >
                            Book <IoIosArrowForward />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Utilization table */}
                {isExpanded && (
                  <div className={`px-5 pt-0 pb-3 ${!isLast ? "border-b border-[#EFEFEF]" : ""}`}>
                    <div className="flex justify-between items-center mb-3">
                      <h1 className="text-sm font-semibold text-gray-900">Membership Utilization</h1>
                      <p className="text-xs text-[#727272]">{membershipVisitDetails?.count ?? 0} records</p>
                    </div>
                    {membershipVisitDetails?.records?.length > 0 ? (
                      <div className="overflow-x-auto border rounded-xl mb-3">
                        <table className="w-full text-xs table-auto">
                          <thead className="bg-[#F6F7F8]">
                            <tr className="border-b border-[#EFEFEF]">
                              <th className="px-4 py-3 text-left font-semibold text-[#193A6A] whitespace-nowrap">
                                Date
                              </th>
                              <th className="px-4 py-3 text-left font-semibold text-[#193A6A] whitespace-nowrap">
                                Time
                              </th>
                              <th className="px-4 py-3 text-left font-semibold text-[#193A6A] whitespace-nowrap">
                                Coupon Type
                              </th>
                              <th className="px-4 py-3 text-left font-semibold text-[#193A6A] whitespace-nowrap">
                                Service
                              </th>
                              <th className="px-4 py-3 text-left font-semibold text-[#193A6A] whitespace-nowrap">
                                Clinic
                              </th>
                              <th className="px-4 py-3 text-left font-semibold text-[#193A6A] whitespace-nowrap">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-[#FBFCFD]">
                            {membershipVisitDetails?.records?.map((record, idx) => (
                              <tr key={idx} className="border-b border-[#EFEFEF] last:border-0">
                                <td className="px-4 py-3 text-[#32363C] font-semibold whitespace-nowrap">
                                  {record?.date}
                                </td>
                                <td className="px-4 py-3 text-[#32363C] font-semibold whitespace-nowrap">
                                  {record?.time}
                                </td>
                                <td className="px-4 py-3 w-[150px]">
                                  <span className="bg-[#F3F8FF] text-[#32363C] py-0.5 px-2 rounded block w-full text-center truncate font-semibold">
                                    {record?.coupon_type}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-[#32363C] font-semibold">{record?.service}</td>
                                <td className="px-4 py-3 text-[#727272] font-semibold">{record?.clinic_name}</td>
                                <td className="px-4 py-3 text-left whitespace-nowrap">
                                  <button
                                    type="button"
                                    onClick={() => handleViewInvoice(record, data)}
                                    className="text-xs font-semibold border border-[#1B68D3] text-[#1B68D3] px-3 py-1.5 rounded-lg hover:bg-blue-50 whitespace-nowrap"
                                  >
                                    View Invoice
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-8 text-sm text-[#727272]">
                        No details found
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MembershipDetailsTab;